/**
 * Unlike most modals, this isn't marked as modal component in its file name, but it is a modal component.
 */

import { Component, OnInit, Input, EventEmitter, Output } from '@angular/core';
import { Router } from '@angular/router';
import { loadStripe } from '@stripe/stripe-js/pure';
import { UntypedFormGroup } from '@angular/forms';
import { animate, transition, state, style, trigger } from '@angular/animations';
import * as moment from 'moment';
import { AddBookForm } from '../../author-dashboard/add-book/add-book-form';
import { InputControlService } from '../../core/services/input-control.service';
import { StripeService } from '../../core/services/stripe.service';
import { MemberRoleService } from '../../core/services/data/member-roles.service';
import { ModalService } from '../../core/services/modal.service';
import { SavedPaymentsMethodsService } from '../../core/services/data/saved-payment-methods.service';
import { environment } from '../../../environments/environment';

interface customWindow extends Window {
	development?: any;
}
declare const window: customWindow;

@Component({
	selector: 'app-add-card',
	templateUrl: './add-card.component.html',
	styleUrls: ['./add-card.component.scss'],
	animations: [
		trigger('fadeOut1', [
			state('true', style({ opacity: '1' })),
			state('false', style({ opacity: '0' })),
			transition('true => false', animate('1s ease-in')),
		]),
		trigger('fadeIn1', [
			state('true', style({ opacity: '1' })),
			state('false', style({ opacity: '0' })),
			transition('false => true', animate('1ms')),
		]),
		trigger('fadeOut2', [
			state('true', style({ opacity: '1' })),
			state('false', style({ opacity: '0' })),
			transition('true => false', animate('1ms')),
		]),
		trigger('fadeIn2', [
			state('true', style({ opacity: '1' })),
			state('false', style({ opacity: '0' })),
			transition('false => true', animate('1s ease-in')),
		]),
	],
})

export class AddCardComponent implements OnInit {
	@Input() customer;

	@Input() changeCard;

	@Input() cardSelected;

	@Input() author;

	@Input() currentSubscription;

	@Input() selectedPlan = '';

	@Input() askForPrimary = true;

	@Output() emitRefreshSuscription = new EventEmitter();

	@Output() emitRefreshListCards = new EventEmitter();

	@Output() refreshUser2 = new EventEmitter();

	@Output() planSelected = new EventEmitter();

	@Output() cardCreated = new EventEmitter();

	public stripe;

	public elements;

	public card;

	public loading = false;

	public optedInToSaveCard = false;

	public confirmText;

	public errorText = [];

	public successText;

	public buttonText = { confirm: 'Confirm', cancel: 'Cancel' };

	public plan;

	public plans;

	public checked = false;

	public available = true;

	public moment = moment;

	public text;

	public form: UntypedFormGroup;

	public formComponents = new AddBookForm();

	public loadingStart = true;

	public hasErrors;

	public dissableButton;

	public hasCustomer = false;

	public hasSubscription = false;

	public btnClicked = false;

	private stripeKey;

	private development;

	private cardRemove;

	constructor(
		private ss: StripeService,
		private mrs: MemberRoleService,
		private ms: ModalService,
		private router: Router,
		private savedPaymentMethodsService: SavedPaymentsMethodsService,
	) {
		this.development = window.development;
	}

	ngOnInit() {
		this.form = InputControlService.toFormGroup(this.formComponents);
		this.loadStripe();
	}

	loadStripe() {
		this.initStripe();
		this.initData();
	}

	// INIT FUNCTIONS

	// Load all needed stripe elements
	async initStripe() {
		this.stripeKey = this.development ? 'stripePKTest' : 'stripePKProd';
		this.stripe = await loadStripe(environment[this.stripeKey]);
		this.elements = this.stripe.elements();
		const cardStyle = {
			base: {
				color: '#32325d',
				fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
				fontSmoothing: 'antialiased',
				fontSize: '16px',
				'::placeholder': {
					color: '#aab7c4',
				},
			},
			invalid: {
				color: '#fa755a',
				iconColor: '#fa755a',
			},
		};
		this.card = this.elements.create('card', { style: cardStyle });
		this.card.mount('#new-card-element');
		this.card.on('focus', () => {
			const el = document.getElementById('card-element-errors');
			el.classList.add('focused');
		});
		this.card.on('blur', () => {
			const el = document.getElementById('card-element-errors');
			el.classList.remove('focused');
		});
		this.card.on('change', (event) => {
			this.displayError(event);
		});
	}

	displayError(event) {
		const _displayError = document.getElementById('card-element-errors');
		if (event.error) {
			_displayError.textContent = event.error.message;
		} else {
			_displayError.textContent = '';
		}
	}

	// Load all needed customer and subscription info
	async initData() {
		// Create empty subscription
		this.plan = {};

		// Create customer if needed
		if (!this.author.stripeCustomerId) {
			this.customer = {
				customerId: '',
				email: this.author.username,
				name: `${this.author.firstName} ${this.author.lastName}`,
				paymentMethodId: '',
			};
			this.loadingStart = false;
		} else {
			this.ss.getCustomer(this.author.stripeCustomerId).subscribe((data: any) => {
				if (data.success) {
					const customerResult = data.customer;
					if (!customerResult.subscription) {
						this.hasSubscription = false;
						this.customer = {
							customerId: this.author.stripeCustomerId,
							email: this.author.username,
							name: `${this.author.firstName} ${this.author.lastName}`,
							paymentMethodId: customerResult.invoice_settings
								? customerResult.invoice_settings.default_payment_method
								: undefined,
						};
						this.hasCustomer = true;
					} else {
						// Handle error
						this.hasSubscription = true;
					}
				}
				this.loadingStart = false;
			});
		}
	}

	// CLICKABLE FUNCTIONS
	makePrimary(event) {
		this.checked = !this.checked;
	}

	async updateCard() {
		this.loading = true;
		this.btnClicked = true;
		// First, create paymentMethod
		const result = await this.stripe.createPaymentMethod({
			type: 'card',
			card: this.card,
		});
		// If paymentMethod fails trigger error and stop
		if (!result || !result.paymentMethod) {
			this.loading = false;
			this.hasErrors = true;
			this.available = true;
			this.btnClicked = false;
			this.errorText = [
				'There was a problem with your credit card data.'
			+ '<br><br>Check the following fields:'
			+ '<br><br>- Card number<br>- Expiration date<br>- CVC Number<br>- C.P. Number'];
			this.ms.open('error-AddCard');
			return;
		}

		if (result.paymentMethod.id) {
			const paymentMethodId = result.paymentMethod.id;
			this.savedPaymentMethodsService.create({ _user: this.author._id, paymentMethodId }).subscribe();
		}

		const paymentMethodId = result.paymentMethod.id;
		this.ss.updateCustomer(this.author.stripeCustomerId, paymentMethodId, true, this.checked).subscribe((data: any) => {
			if (data.success) {
				this.loading = false;
				this.btnClicked = false;
				this.emitRefreshListCards.emit({ default: this.checked });

				if (!this.askForPrimary) {
					this.close();
					this.cardCreated.emit(result.paymentMethod);
					return;
				}

				this.successText = 'Card added successfully';
				this.ms.open('success-addCard-modal');
				this.ss.retrySubscriptionPayment(this.currentSubscription._id)
					.subscribe(this.handleRetrySubscriptionPayment.bind(this));
			} else {
				this.loading = false;
				this.btnClicked = false;
			}
		});
	}

	handleRetrySubscriptionPayment(data) {
		if (data && data.success) {
			this.emitRefreshSuscription.emit();
		}
	}

	closeModal(event) {
		this.ms.close('update-card-form');
	}

	save(event) {
		event.preventDefault();
		this.errorText = [];
	}

	cancel() {
		if (this.form.pristine) {
			this.close();
		} else {
			this.successText = 'Card added successfully';
			this.ms.open('success-modal');
		}
	}

	handleConfirm(event) {
		if (event) {
			this.close();
		}
	}

	close() {
		this.card.clear();
		this.ms.close('add-card-form');
		this.ms.open('list-cards');
	}

	refreshUser() {
		this.refreshUser2.emit();
	}

	goToPlans() {
		this.ms.close('update-card-form');
		this.router.navigate([`/author-dashboard/${this.author.authorLink}/subscriptions`]).then();
	}
}
